import type { Key9Puzzle } from '../types/puzzle';

export type Key9AttemptState = {
  misses: number;
  maxMisses: number;
  lockedCells: Map<number, number>;
  usedTiles: Set<number>;
};

export type Key9AttemptResult =
  | {
      kind: 'correct';
      digit: number;
      completed: boolean;
      state: Key9AttemptState;
    }
  | {
      kind: 'wrong';
      digit: number;
      missed: boolean;
      state: Key9AttemptState;
    }
  | {
      kind: 'ignored';
      reason: 'used_tile' | 'locked_cell';
      state: Key9AttemptState;
    };

export type Key9CellTapDecision =
  | {
      kind: 'place';
      cellIndex: number;
      tileIndex: number;
      state: Key9AttemptState;
    }
  | {
      kind: 'needs_tile';
      state: Key9AttemptState;
    }
  | {
      kind: 'ignored';
      reason: 'non_key_cell' | 'locked_cell';
      state: Key9AttemptState;
    };

export function resolveKey9CellTap(
  puzzle: Key9Puzzle,
  state: Key9AttemptState,
  cellIndex: number,
  selectedTileIndex: number | null
): Key9CellTapDecision {
  if (!puzzle.keyCells.includes(cellIndex)) {
    return { kind: 'ignored', reason: 'non_key_cell', state };
  }

  if (state.lockedCells.has(cellIndex)) {
    return { kind: 'ignored', reason: 'locked_cell', state };
  }

  if (selectedTileIndex == null) {
    return { kind: 'needs_tile', state };
  }

  return {
    kind: 'place',
    cellIndex,
    tileIndex: selectedTileIndex,
    state
  };
}

export function applyKey9Attempt(
  puzzle: Key9Puzzle,
  state: Key9AttemptState,
  cellIndex: number,
  tileIndex: number
): Key9AttemptResult {
  if (state.usedTiles.has(tileIndex)) {
    return { kind: 'ignored', reason: 'used_tile', state };
  }

  if (state.lockedCells.has(cellIndex)) {
    return { kind: 'ignored', reason: 'locked_cell', state };
  }

  const digit = puzzle.keyTiles[tileIndex];
  const correct = puzzle.solution[cellIndex] === String(digit);

  if (correct) {
    const lockedCells = new Map(state.lockedCells);
    const usedTiles = new Set(state.usedTiles);
    lockedCells.set(cellIndex, digit);
    usedTiles.add(tileIndex);

    const nextState = {
      ...state,
      lockedCells,
      usedTiles
    };

    return {
      kind: 'correct',
      digit,
      completed: lockedCells.size >= puzzle.keyCells.length,
      state: nextState
    };
  }

  const misses = Math.min(state.maxMisses, state.misses + 1);
  return {
    kind: 'wrong',
    digit,
    missed: misses >= state.maxMisses,
    state: {
      ...state,
      misses
    }
  };
}
